import React, { FC, useState } from 'react';
import type { CvtDiagnosticEntity } from '@packages/domain/cvt';
import { createUseStyles } from 'react-jss';
import { DateTime } from 'luxon';
import { DetailViewAside } from '../../components/detail-view/detail-view-aside';
import { StyleSheet, View } from 'react-native';
import { Tab, Tabs } from 'react-bootstrap';
import { Typography } from '@packages/ui/shared/typography';
import Avatar from '../../components/avatar';

interface DetailsCardProps {
    content?: string;
    subTitle?: string;
    title: string;
}
const styles = StyleSheet.create({
    section: {
        paddingBottom: 12,
    },
    labelWrapper: {
        alignItems: 'baseline',
        flexDirection: 'row',
    },
    title: {
        fontSize: 15,
        fontWeight: '600',
        lineHeight: 24,
    },
    subTitle: {
        fontSize: 15,
        fontWeight: '400',
        lineHeight: 24,
        marginTop: 8,
        textTransform: 'uppercase',
    },
    content: {
        fontWeight: '400',
        lineHeight: 24,
    },
});
const DetailsCard: FC<DetailsCardProps> = ({ title, subTitle, content }) => (
    <View style={styles.section}>
        {title && (
            <Typography variant="h6" style={styles.title}>
                {title}
            </Typography>
        )}

        {subTitle && (
            <Typography style={styles.subTitle} color="blueOne">
                {subTitle}
            </Typography>
        )}
        {content && <Typography style={styles.content}>{content}</Typography>}
    </View>
);

const styless = StyleSheet.create({
    userContainer: {
        minHeight: 82,
        paddingVertical: 12,
    },
    avatarWrapper: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 8,
    },
    avatarNameWrapper: {
        marginLeft: 16,
    },
});

interface UsersDetailsProps {
    avatar?: { firstName?: string; lastName?: string; imageUrl?: string };
    label: string | JSX.Element;
    name: string;
    date?: string;
}

export const UsersDetails: FC<UsersDetailsProps> = ({ avatar, label, name, date }) => {
    return (
        <View style={[styless.userContainer]}>
            {typeof label === 'string' ? <Typography variant="h6">{label}</Typography> : <View>{label}</View>}

            <View style={styless.avatarWrapper}>
                <Avatar size={32} {...avatar} />
                <View style={styless.avatarNameWrapper}>
                    <Typography color="blueOne">{name}</Typography>

                    {!!date && <Typography variant="caption">{date}</Typography>}
                </View>
            </View>
        </View>
    );
};

const useRightSideAsideStyles = createUseStyles({
    asideContainer: {
        padding: 0,
        height: '100%',
    },
    content: {
        padding: '12px 24px',
    },
});

type Props = {
    accountName?: string;
    jobCompletedTimestamp?: string;
    retailer?: CvtDiagnosticEntity['retailer'];
};
const CvtRightAside: FC<Props> = ({ retailer, accountName, jobCompletedTimestamp }) => {
    const [tab, setTab] = useState<string>('DETAILS');
    const classes = useRightSideAsideStyles();
    return (
        <DetailViewAside classNames={classes.asideContainer}>
            <Tabs
                style={{ paddingLeft: 24, paddingRight: 24, paddingTop: 16 }}
                activeKey={tab}
                onSelect={(t) => {
                    if (t) {
                        setTab(t);
                    }
                }}
                id="cvt-details-aside-tabs"
                mountOnEnter
                unmountOnExit
            >
                <Tab
                    eventKey="DETAILS"
                    title={
                        <Typography variant="label" color="blueOne" numberOfLines={1}>
                            Details
                        </Typography>
                    }
                >
                    <div className={classes.content}>
                        {retailer && (
                            <DetailsCard
                                title={'Retailer'}
                                subTitle={retailer ? `${retailer?.name} (#${retailer?.code})` : ''}
                                content={
                                    retailer
                                        ? `Region ${retailer?.region}, Zone ${retailer?.zone}, District ${retailer?.district}`
                                        : ''
                                }
                            />
                        )}
                        <UsersDetails
                            label={'Tested By'}
                            name={accountName || ''}
                            date={
                                jobCompletedTimestamp
                                    ? DateTime.fromISO(jobCompletedTimestamp).toFormat("MMM d, yyyy 'at' h:mm a")
                                    : ''
                            }
                        />
                    </div>
                </Tab>
            </Tabs>
        </DetailViewAside>
    );
};

export default CvtRightAside;
